import React from 'react'
import { Pressable, ScrollView, Text, View } from 'react-native'
import type { GameResponse } from '@warcarp/api-types'
import { GameRow } from '../components/GameRow'
import { useTheme } from '../hooks/useTheme'
import { HomeHeader } from '../components/home/HomeHeader'
import { DesktopCommandColumn } from '../components/home/DesktopCommandColumn'
import {
  HOME_SAMPLE_COMPLETED, HOME_SAMPLE_INVITES, HOME_SAMPLE_OPEN, HOME_SAMPLE_TURNS,
  HOME_SAMPLE_USER_ID, HOME_SAMPLE_WAITING, type DesktopHomeConcept,
} from './desktopHomeConcepts'

export function DesktopHomeSample({ concept, completed, onTabChange, onAction, onGamePress }: {
  concept: DesktopHomeConcept; completed: boolean; onTabChange: (completed: boolean) => void
  onAction: (action: string) => void; onGamePress: (game: GameResponse) => void
}) {
  const { theme } = useTheme()
  const dark = theme === 'dark'
  const ink = dark ? '#e6e1c4' : '#18170f'
  const soft = dark ? '#b4b79e' : '#71674b'
  const olive = dark ? '#8aa14a' : '#4a5a2a'
  const blood = dark ? '#e0463f' : '#a01818'
  const canvas = dark ? '#1b2213' : '#eee3c1'
  const paper = dark ? '#2d351f' : '#f4ecd3'
  const [width, setWidth] = React.useState(1024)
  const wide = width >= 720
  const compact = concept.id === 'compact-roster'
  const rail = wide && !completed && ['command-column', 'frontline'].includes(concept.id)
  const openGameListVisible = rail || (wide && concept.id === 'command-column')
  const grid = wide && concept.id === 'card-table'
  const board = wide && !completed && concept.id === 'turn-board'
  const frontline = !completed && concept.id === 'frontline'
  const maxWidth = concept.id === 'field-desk' ? 960 : concept.id === 'turn-board' ? 1240 : compact ? 1080 : 1160
  const gridWidth = (Math.min(width - (wide ? 48 : 32), maxWidth) - (rail ? 302 : 0) - 12) / 2
  const frame = { width: '100%' as const, maxWidth, alignSelf: 'center' as const }
  const action = (label: string, primary = false) => <Pressable key={label} accessibilityRole="button" onPress={() => onAction(label)}
    style={{ minHeight: 44, paddingHorizontal: 20, paddingVertical: 12, borderRadius: 9,
      borderWidth: 1, borderColor: primary ? blood : olive, backgroundColor: primary ? blood : paper,
      flex: 1, alignItems: 'center', justifyContent: 'center' }}>
    <Text className="font-stencil" style={{ fontSize: 13, color: primary ? '#fff' : ink }}>{label}</Text>
  </Pressable>
  const row = (game: GameResponse) => <GameRow key={game.id} game={game} currentUserId={HOME_SAMPLE_USER_ID}
    trackUnread={false} loadMapFromNetwork={false} showMapPreview density={compact ? 'compact' : 'normal'} onPress={() => onGamePress(game)} />
  const section = (title: string, games: GameResponse[], columns = false) => <View style={{ gap: 10 }}>
    <Text className="font-stencil" style={{ fontSize: 12, color: olive }}>{title} · {games.length}</Text>
    <View style={{ flexDirection: columns ? 'row' : 'column', flexWrap: columns ? 'wrap' : undefined, gap: 12 }}>
      {games.map(game => <View key={game.id} style={columns ? { width: gridWidth } : undefined}>{row(game)}</View>)}
    </View>
  </View>
  const all = completed ? HOME_SAMPLE_COMPLETED : [...HOME_SAMPLE_TURNS, ...HOME_SAMPLE_INVITES, ...HOME_SAMPLE_WAITING]
  const tabs = (
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <View style={{ flexDirection: 'row', borderWidth: 1, borderColor: olive, borderRadius: 22, padding: 4 }}>
            {(['Current', 'Completed'] as const).map((label, index) => <Pressable key={label} accessibilityRole="button"
              accessibilityState={{ selected: completed === Boolean(index) }} onPress={() => onTabChange(Boolean(index))}
              style={{ paddingHorizontal: 18, paddingVertical: 9, borderRadius: 18, backgroundColor: completed === Boolean(index) ? blood : 'transparent' }}>
              <Text className="font-stencil" style={{ fontSize: 12, color: completed === Boolean(index) ? '#fff' : soft }}>{label}</Text>
            </Pressable>)}
          </View>
          {!openGameListVisible && <Pressable accessibilityRole="button" onPress={() => onAction('Join Game')}
            style={{ borderWidth: 1, borderColor: olive, borderRadius: 22, paddingHorizontal: 16, paddingVertical: 10, backgroundColor: paper }}>
            <Text className="font-stencil" style={{ color: blood, fontSize: 11 }}>♟ 1 game needs players »</Text>
          </Pressable>}
        </View>
  )
  if (concept.id === 'command-column' && wide) return <View onLayout={event => setWidth(event.nativeEvent.layout.width)} style={{ flex: 1, backgroundColor: canvas }}>
    <HomeHeader displayName="yakuza" desktop={wide} maxWidth={maxWidth}
      onMaps={() => onAction('Maps')} onBots={() => onAction('Bots')}
      onHallOfFame={() => onAction('Hall of fame')} onSettings={() => onAction('Settings')} />
    <DesktopCommandColumn sample games={all} completed={completed} currentUserId={HOME_SAMPLE_USER_ID}
      openGames={HOME_SAMPLE_OPEN} tabs={tabs} onGamePress={onGamePress}
      onNewGame={() => onAction('New Game')} onJoinGame={() => onAction('Join Game')} />
  </View>
  return <View onLayout={event => setWidth(event.nativeEvent.layout.width)} style={{ flex: 1, backgroundColor: canvas }}>
    <HomeHeader displayName="yakuza" desktop={wide} maxWidth={maxWidth}
      onMaps={() => onAction('Maps')} onBots={() => onAction('Bots')}
      onHallOfFame={() => onAction('Hall of fame')} onSettings={() => onAction('Settings')} />
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: wide ? 24 : 16, gap: 20 }}>
      <View style={[frame, { gap: 22 }]}>
        {tabs}
        <View style={{ flexDirection: 'row', gap: 22, alignItems: 'flex-start' }}>
          <View style={{ flex: 1, minWidth: 0, gap: 24 }}>
            {board ? <View style={{ flexDirection: 'row', gap: 16 }}>
              {[['Your turn', HOME_SAMPLE_TURNS], ['Waiting', HOME_SAMPLE_WAITING], ['Invitations', HOME_SAMPLE_INVITES]].map(([title, games]) =>
                <View key={String(title)} style={{ flex: 1, minWidth: 0 }}>{section(String(title), games as GameResponse[])}</View>)}
            </View> : frontline ? <>{section('Your turn', HOME_SAMPLE_TURNS, wide)}{section('Invitations', HOME_SAMPLE_INVITES)}{section('Waiting for others', HOME_SAMPLE_WAITING)}</>
              : grid ? section(completed ? 'Completed campaigns' : 'Your games', all, true)
                : compact ? section(completed ? 'Completed campaigns' : 'Current campaigns', all)
                  : concept.id === 'command-column' && !completed ? <>{section('Your turn', HOME_SAMPLE_TURNS)}{section('Invitations', HOME_SAMPLE_INVITES)}{section('Waiting for others', HOME_SAMPLE_WAITING)}</>
                    : <View style={{ gap: 12 }}>{all.map(row)}</View>}
          </View>
          {rail && <View style={{ width: 280, gap: 18 }}>
            <View style={{ backgroundColor: paper, borderWidth: 1, borderColor: olive, borderRadius: 12, padding: 16, gap: 12 }}>
              <Text className="font-stencil" style={{ color: ink, fontSize: 13 }}>Ready for orders</Text>
              <Text className="font-typed" style={{ color: soft, fontSize: 15 }}>Two turns to play and one invitation to answer.</Text>
              {action('New Game', true)}{action('Join Game')}
            </View>
            {section('Open game', HOME_SAMPLE_OPEN)}
          </View>}
        </View>
      </View>
    </ScrollView>
    {!rail && <View style={{ borderTopWidth: 1, borderColor: olive, backgroundColor: paper, paddingHorizontal: 24, paddingVertical: 14 }}>
      <View style={[frame, { flexDirection: 'row', gap: 12 }]}>{action('New Game', true)}{action('Join Game')}</View>
    </View>}
  </View>
}
